import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger, Icon } from "@qw/ui";
import type { ReasoningUIPart, SourceDocumentUIPart, SourceUrlUIPart, TextUIPart } from "ai";
import { createContext, type FC, type ReactNode, useContext, useEffect, useState } from "react";
import remarkGfm from "remark-gfm";

import type { AssistantMessageProgress } from "../assistant-progress-state";
import { isAssistantMessageContentPart, isLegacyObjectObjectTextPart, isToolMessagePart } from "../chat.helpers";
import {
  ChatMessageStatus,
  type ExtendedUIMessage,
  type MessagePart,
  type MessagePartToolInvocation,
  type SuggestionInsertSlot,
} from "../chat.types";
import { useSequentialPartReveal } from "../hooks/use-sequential-part-reveal";
import { extractDefaultSuggestionSegments } from "../suggestion-insert";
import { MarkdownComponents } from "../utils/markdown-components";
import { ChatErrorDisclosure, getChatErrorDetail } from "./chat-error-disclosure";
import { ChatInterruptedDisclosure } from "./chat-interrupted-disclosure";
import { ChatMessage, ChatMessageContent, ChatMessageFooter } from "./chat-message";
import { ChatSessionMessageToolInvocation } from "./chat-session-message-tool-invocation";
import { StreamedMarkdown } from "./streamed-markdown";

/**
 * Whether the enclosing message is the actively-streaming turn. `ChatContent`
 * resolves this once as part of the progress presentation and passes it through
 * `AssistantChatSessionMessage`, so parts can gate smooth reveal without reading
 * global chat state or independently deriving ownership.
 */
const MessageStreamingContext = createContext(false);
const useMessageStreaming = (): boolean => useContext(MessageStreamingContext);

/**
 * The parts whose text is revealed by the smooth typewriter: `text` (excluding the
 * legacy object-object error part, which renders a fixed message, not `part.text`) and
 * `reasoning`. Everything else (tools, sources, files) renders immediately. These are
 * sequenced by one message-level {@link useSequentialPartReveal} cursor so a later part
 * never types while an earlier one is still revealing.
 *
 * The `reasoning` branch is inert while `RENDER_REASONING_PARTS` is off — those parts are
 * filtered out before this predicate sees them, so they consume no cursor slot and the
 * answer text reveals without waiting on them.
 */
const isRevealablePart = (part: MessagePart): part is TextUIPart | ReasoningUIPart =>
  (part.type === "text" && !isLegacyObjectObjectTextPart(part)) || part.type === "reasoning";

export const AssistantChatSessionMessage = ({
  message,
  suggestionInsert,
  progress,
}: {
  message: ExtendedUIMessage;
  suggestionInsert?: SuggestionInsertSlot;
  progress?: AssistantMessageProgress;
}) => {
  const contentParts = message.parts?.filter(isAssistantMessageContentPart) ?? [];
  const statusDisclosure = renderMessageStatusDisclosure(message.status, contentParts);

  // Don't show a message if it has nothing to display: no renderable parts, terminal-status
  // disclosure, or progress footer. Terminal turns can carry only non-content parts, and a
  // reasoning-only turn can become the last persisted message while the SDK is still in a
  // non-streaming wait state; both still need their disclosure/footer to remain visible.
  if (!contentParts.length && !statusDisclosure && !progress) {
    return null;
  }

  return (
    <ChatMessage>
      <ChatMessageContent>
        {statusDisclosure ?? (
          <MessageStreamingContext.Provider value={progress?.isStreaming ?? false}>
            <AssistantChatSessionMessageParts
              parts={contentParts}
              message={message}
              suggestionInsert={suggestionInsert}
            />
          </MessageStreamingContext.Provider>
        )}
      </ChatMessageContent>
      {progress && <ChatMessageFooter>{progress.label}</ChatMessageFooter>}
    </ChatMessage>
  );
};

const getAssistantErrorText = (parts: MessagePart[]): string | null => {
  const text = parts
    .filter((part): part is TextUIPart => part.type === "text" && Boolean(part.text?.trim()))
    .map((part) => part.text.trim())
    .join("\n\n");

  return text || null;
};

/**
 * Maps a terminal message status to its disclosure, or `null` when the status
 * has no special rendering (the parts render normally). Centralizes the
 * status→UI dispatch so new statuses extend this one switch rather than growing
 * ad-hoc string branches in the renderer.
 */
const renderMessageStatusDisclosure = (
  status: ExtendedUIMessage["status"],
  contentParts: MessagePart[]
): ReactNode | null => {
  switch (status) {
    case ChatMessageStatus.Error:
      return <ChatErrorDisclosure detail={getChatErrorDetail(getAssistantErrorText(contentParts))} />;
    case ChatMessageStatus.Interrupted:
      return <ChatInterruptedDisclosure detail={getAssistantErrorText(contentParts)} />;
    default:
      return null;
  }
};

interface AssistantChatSessionMessagePartsProps {
  parts: MessagePart[];
  message: ExtendedUIMessage;
  suggestionInsert?: SuggestionInsertSlot;
}

const AssistantChatSessionMessageParts: FC<AssistantChatSessionMessagePartsProps> = ({
  parts,
  message,
  suggestionInsert,
}) => {
  const messageStreaming = useMessageStreaming();

  // Reveal the text/reasoning parts through ONE message-level cursor, in document order, so a later
  // part can't begin its typewriter until every earlier part has finished revealing (no reasoning↔
  // answer overlap). Non-revealable parts (tools, sources) are unaffected and render immediately.
  const revealableParts = parts.filter(isRevealablePart);
  const revealedTexts = useSequentialPartReveal(
    revealableParts.map((part) => ({ text: part.text, isDone: part.state === "done" })),
    messageStreaming
  );
  const displayTextByPart = new Map<MessagePart, string>();
  revealableParts.forEach((part, index) => {
    displayTextByPart.set(part, revealedTexts[index]);
  });

  return parts.map((part, index) => (
    <AssistantChatSessionMessagePart
      key={index}
      part={part}
      message={message}
      suggestionInsert={suggestionInsert}
      displayText={displayTextByPart.get(part)}
    />
  ));
};

interface AssistantChatSessionMessagePartProps {
  part: MessagePart;
  message: ExtendedUIMessage;
  suggestionInsert?: SuggestionInsertSlot;
  /** The revealed prefix for a revealable (text/reasoning) part, from the message-level
   *  sequential reveal. `undefined` for non-revealable parts. */
  displayText?: string;
}

const AssistantChatSessionMessagePart: FC<AssistantChatSessionMessagePartProps> = ({
  part,
  message,
  suggestionInsert,
  displayText,
}) => {
  switch (part.type) {
    case "text":
      return (
        <AssistantTextChatSessionMessagePart
          part={part}
          message={message}
          suggestionInsert={suggestionInsert}
          displayText={displayText ?? part.text}
        />
      );

    case "source-url":
      return <AssistantSourceChatSessionMessagePart part={part} message={message} />;

    case "source-document":
      return <AssistantSourceDocumentChatSessionMessagePart part={part} message={message} />;

    // Unreachable while `RENDER_REASONING_PARTS` is off: reasoning parts are filtered out
    // upstream by `isAssistantMessageContentPart`, so they never reach this switch. Kept wired
    // so flipping that constant restores the disclosure with no other change.
    case "reasoning":
      return (
        <AssistantReasoningChatSessionMessagePart
          part={part}
          message={message}
          displayText={displayText ?? part.text}
        />
      );

    case "file":
      // NOTE: We don't currently have a use case for displaying file parts in the assistant message parts.
      return null;

    default:
      if (isToolMessagePart(part)) {
        return <AssistantToolInvocationChatSessionMessagePart part={part} message={message} />;
      }

      return null;
  }
};

interface AssistantTextChatSessionMessagePartProps extends AssistantChatSessionMessagePartProps {
  part: TextUIPart;
}

const AssistantTextChatSessionMessagePart: FC<AssistantTextChatSessionMessagePartProps> = ({
  part,
  suggestionInsert,
  displayText = part.text,
}) => {
  // `displayText` is the message-level sequential reveal's prefix for this part (full text for
  // historical/finished/reduced-motion, and while non-streaming). See `useSequentialPartReveal`.
  if (isLegacyObjectObjectTextPart(part)) {
    return (
      <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-sm text-destructive">
        <Icon icon="error" className="mt-0.5 text-base" />
        <span>Alby hit an error while generating this response.</span>
      </div>
    );
  }

  // The suggestion-insert buttons are gated behind the reveal via `afterComplete`: `StreamedMarkdown`
  // only renders them once `displayText === part.text`. The extractor parses the whole `part.text`, so
  // without that gate a sequential-reveal turn (`reasoning → answer`) could render buttons under a
  // still-blank/partial answer while an earlier part drains. Same completion check the reasoning
  // collapse uses.
  return (
    <StreamedMarkdown
      displayText={displayText}
      fullText={part.text}
      components={MarkdownComponents}
      remarkPlugins={[remarkGfm]}
      afterComplete={<AssistantSuggestionInsertButtons part={part} suggestionInsert={suggestionInsert} />}
    />
  );
};

const AssistantSuggestionInsertButtons = ({
  part,
  suggestionInsert,
}: {
  part: TextUIPart;
  suggestionInsert?: SuggestionInsertSlot;
}) => {
  if (!suggestionInsert) {
    return null;
  }

  const segments = (suggestionInsert.extract ?? extractDefaultSuggestionSegments)(part);

  if (segments.length === 0) {
    return null;
  }

  return (
    <div className="not-prose mt-2 flex flex-wrap gap-1.5">
      {segments.map((segment, index) => (
        <Button
          key={`${index}-${segment.text.slice(0, 24)}`}
          type="button"
          variant="secondary"
          size="xs"
          onClick={() => suggestionInsert.onInsert(segment.text)}
        >
          <Icon icon="add" />
          <span>Insert</span>
        </Button>
      ))}
    </div>
  );
};

interface AssistantSourceChatSessionMessagePartProps extends AssistantChatSessionMessagePartProps {
  part: SourceUrlUIPart;
}

const AssistantSourceChatSessionMessagePart: FC<AssistantSourceChatSessionMessagePartProps> = ({ part }) => {
  return <p>{part.url}</p>;
};

interface AssistantSourceDocumentChatSessionMessagePartProps extends AssistantChatSessionMessagePartProps {
  part: SourceDocumentUIPart;
}

const AssistantSourceDocumentChatSessionMessagePart: FC<AssistantSourceDocumentChatSessionMessagePartProps> = ({
  part,
}) => {
  return (
    <div className="flex max-w-full items-center gap-1.5 text-xs text-muted-foreground">
      <Icon icon="description" className="text-sm" />
      <span className="truncate">{part.title || part.filename || "Source document"}</span>
    </div>
  );
};

interface AssistantReasoningChatSessionMessagePartProps extends AssistantChatSessionMessagePartProps {
  part: ReasoningUIPart;
}

const AssistantReasoningChatSessionMessagePart: FC<AssistantReasoningChatSessionMessagePartProps> = ({
  part,
  displayText = part.text,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(() => part.state !== "done");

  // `displayText` is the message-level sequential reveal's prefix for this reasoning part. It keeps
  // advancing after `part.state` flips `done` (the cursor drains the buffered tail, then moves on to
  // the next part), so the collapse below waits for the tail via `revealComplete`.

  // Auto-open while streaming; collapse on `done` only once the reveal has
  // caught up (`displayText === part.text`) so the buffered tail is readable.
  // While the tail is still draining after `done`, neither branch fires, so the
  // disclosure stays open. Non-animating (reduced-motion / historical) reveals
  // are already complete, so this collapses immediately.
  const revealComplete = displayText === part.text;
  useEffect(() => {
    if (part.state === "streaming") {
      setIsOpen(true);
    } else if (part.state === "done" && revealComplete) {
      setIsOpen(false);
    }
  }, [part.state, revealComplete]);

  // Don't render anything if there's no reasoning text
  if (!part.text) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="my-1">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="xs" className="group text-xs -m-1.5 -mb-1 text-muted-foreground">
          <Icon icon="psychology" />
          <span>Reasoning{part.state === "streaming" && <>&hellip;</>}</span>
          <Icon icon="keyboard_arrow_down" className="transition-transform group-data-[state=open]:rotate-180" />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-1">
        <div className="text-xs text-muted-foreground">{displayText}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

interface AssistantToolInvocationChatSessionMessagePartProps extends AssistantChatSessionMessagePartProps {
  part: MessagePartToolInvocation;
}

const AssistantToolInvocationChatSessionMessagePart: FC<AssistantToolInvocationChatSessionMessagePartProps> = ({
  part,
  message,
}) => {
  // Child playbook messages are now merged at the session level via useChildPlaybookMessages
  // in chat-session-messages.tsx, so no inline rendering is needed here
  return <ChatSessionMessageToolInvocation toolInvocation={part} message={message} />;
};
